import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { HeaderList } from '@/components/lists/HeaderList/HeaderList';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { isBeneficiaryDesignationEntityType } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { SpecifyDispositiveProvisionsButton } from '@/modules/dispositiveProvisions/SpecifyDispositiveProvisionsButton';
import { getNodes } from '@/utils/graphqlUtils';

import {
  DispositiveProvisions_DispositionScenarioFragment,
  DispositiveProvisions_DispositiveProvisionFragment,
} from '../graphql/DispositiveProvisions.fragments.generated';
import { DispositiveProvisionDirection } from './DispositiveProvisionsByDeath.types';
import { sortDispositiveProvisions } from './DispositiveProvisionsListView.utils';
import { DispositiveProvisionRow } from './DispositiveProvisionsRow';
import { ActiveDispositionTab } from './hooks/useDispositiveProvisionsListViewTab';

const useHeaderListClasses = makeStyles()(() => ({
  // eslint-disable-next-line tss-unused-classes/unused-classes
  root: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childContainer: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childWrapper: {
    padding: '0px 12px 0 0',
  },
}));

type DispositiveProvisionsForSingleClientHouseholdProps = {
  dispositionScenarioForTab?: DispositiveProvisions_DispositionScenarioFragment | null;
  currentNodeValue?: Decimal;
  dispositiveProvisions?: DispositiveProvisions_DispositiveProvisionFragment[];
} & ActiveDispositionTab;

export function DispositiveProvisionsForSingleClientHousehold({
  dispositionScenarioForTab,
  currentNodeValue,
  dispositiveProvisions,
  activePrimaryClientIdTab,
  setActivePrimaryClientIdTab,
  defaultTab,
}: DispositiveProvisionsForSingleClientHouseholdProps) {
  const { classes: headerListClasses } = useHeaderListClasses();
  const { primaryClients, entityType } = useDispositiveProvisionsContext();
  const isBenDesignation = isBeneficiaryDesignationEntityType(entityType);

  const items = useMemo(() => {
    if (dispositiveProvisions) {
      return dispositiveProvisions.sort(sortDispositiveProvisions);
    }

    if (dispositionScenarioForTab?.dispositiveProvisionsTemplate) {
      return getNodes(
        dispositionScenarioForTab?.dispositiveProvisionsTemplate
          .dispositiveProvisions
      ).sort(sortDispositiveProvisions);
    }

    return getNodes(dispositionScenarioForTab?.dispositiveProvisions).sort(
      sortDispositiveProvisions
    );
  }, [
    dispositiveProvisions,
    dispositionScenarioForTab?.dispositiveProvisionsTemplate,
    dispositionScenarioForTab?.dispositiveProvisions,
  ]);

  const normalizedDispositionScenariosFirstDeath = {
    heading: 'Upon first death',
    noItemsText: isBenDesignation
      ? 'No beneficiary designations specified'
      : 'No dispositions specified upon first death',
    items,
  };

  return (
    <Stack spacing={2}>
      <Box>
        <HeaderList
          heading={normalizedDispositionScenariosFirstDeath.heading}
          noItemsText={normalizedDispositionScenariosFirstDeath.noItemsText}
          classes={headerListClasses}
        >
          {normalizedDispositionScenariosFirstDeath.items.map((item) => (
            <DispositiveProvisionRow
              primaryClients={primaryClients}
              key={item.id}
              provision={item}
              dyingPrimaryClientId={activePrimaryClientIdTab}
              calculatedProvisionAmount={null}
              direction={DispositiveProvisionDirection.Distributing}
            />
          ))}
        </HeaderList>
      </Box>
      <SpecifyDispositiveProvisionsButton
        buttonProps={{
          fullWidth: true,
          sx: {
            px: 2,
          },
        }}
        behavior="edit"
        currentNodeValue={currentNodeValue}
        activePrimaryClientIdTab={activePrimaryClientIdTab}
        setActivePrimaryClientIdTab={setActivePrimaryClientIdTab}
        defaultTab={defaultTab}
      />
    </Stack>
  );
}
